import { registerPlugin, PluginListenerHandle } from '@capacitor/core';

export interface WatchMessage {
  action: 'startSharing' | 'stopSharing' | 'sharingFailed' | 'sharingStopped';
}

export interface GarminConnectIQPlugin {
  sendCredentials(options: { userId: string; jwt: string }): Promise<{
    success: boolean;
    message: string;
  }>;

  /**
   * Starts the native background location service with the supplied credentials.
   * On Android this launches a foreground service that survives the lock screen.
   * On iOS this starts a CLLocationManager with background location enabled.
   * Safe to call from a JS watch-message handler.
   */
  startLocationSharing(options: { jwt: string; userId: string }): Promise<void>;

  /** Stops the native background location service. */
  stopLocationSharing(): Promise<void>;

  /**
   * Call when the app returns to the foreground. If a share was requested
   * while the app was backgrounded and could not start the service, this
   * starts it now. Resolves { resumed: true } if sharing was auto-started.
   */
  resumePendingShare(): Promise<{ resumed: boolean }>;

  addListener(
    eventName: 'watchMessage',
    listenerFunc: (data: WatchMessage) => void
  ): Promise<PluginListenerHandle> & PluginListenerHandle;

  removeAllListeners(): Promise<void>;
}

const GarminConnectIQ = registerPlugin<GarminConnectIQPlugin>('GarminConnectIQ', {
  web: () =>
    Promise.resolve({
      async sendCredentials(_opts: { userId: string; jwt: string }) {
        return { success: false, message: 'GarminConnectIQ not available on web' };
      },
      async startLocationSharing(_opts: { jwt: string; userId: string }) {},
      async stopLocationSharing() {},
      async resumePendingShare() { return { resumed: false }; },
      async addListener(_event: string, _fn: (data: WatchMessage) => void) {
        return { remove: async () => {} };
      },
      async removeAllListeners() {},
    }),
});

export { GarminConnectIQ };
